import GridSpinner from 'components/UI/GridSpinner'
import ShowMoreButton from 'components/ShowMoreButton'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styles from 'modules/Recommendations/Audits/audits.module.scss'
import GroupActions from './subcomponents/GroupActions'
import List from './subcomponents/List'
import FilterPanel from 'components/Filters/FilterPanel'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Recommendations/Audits/filters'
import {
  DROP_LIST,
  FETCH,
  SET_FILTER,
  TOGGLE_ARCHIVE,
  UPDATE,
} from 'modules/Recommendations/Audits/store/types'
import PageHeader from 'components/PageHeader'
import useTotallyEmptyList from 'utils/useTotallyEmptyList'
import omit from 'lodash/omit'
import EmptyResults from 'components/EmptyResults'
import { Icon } from 'gu-ui-kit'

const RecommendationsList = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [page, setPage] = useState(1)
  const [checkedIDs, setCheckedIDs] = useState([])

  const { data, pagination, loading, archive, loaded } = useSelector(
    (state) => state['recommendations/audits'],
  )

  const filterManager = useFiltersManager({
    filters: useSelector((state) => state['recommendations/audits'].filters),
    config: config(),
    setAction: SET_FILTER,
    localStorageKey: 'recommendations/audits',
  })

  useEffect(() => {
    return () => {
      dispatch({ type: UPDATE, payload: { loading: true, loaded: false } })
    }
  }, [])

  useEffect(() => {
    dispatch({ type: DROP_LIST })
    setPage(1)
    setCheckedIDs([])
  }, [filterManager.filters])

  useEffect(() => {
    if (!filterManager.params) return

    setCheckedIDs([])

    dispatch({
      type: FETCH,
      payload: {
        params: {
          ...filterManager.params,
          offset: pagination.limit * (page - 1),
          limit: pagination.limit,
        },
      },
    })
  }, [filterManager.params, page])

  const handleChangePage = useCallback(() => {
    setPage((page) => page + 1)
  }, [setPage])

  const checkboxHandleChange = useCallback(
    (e, id = null) => {
      if (!id) {
        setCheckedIDs(e.target.checked ? data.map(({ id }) => id) : [])
      } else {
        if (checkedIDs.includes(id)) {
          setCheckedIDs(checkedIDs.filter((checkedID) => checkedID !== id))
        } else {
          const newChecked = checkedIDs.concat(id)
          setCheckedIDs(newChecked)
        }
      }
    },
    [checkedIDs, setCheckedIDs, data],
  )

  const action = useMemo(
    () => (filterManager.filters?.status === 'active' ? 'archive' : 'activate'),
    [filterManager.filters],
  )

  const [totallyEmptyList, firstLoad] = useTotallyEmptyList(
    omit(filterManager.params, ['status']),
    data,
    loaded,
    loading,
  )

  return (
    <div className={'layout-tile'}>
      {firstLoad ? (
        <GridSpinner width={32} />
      ) : totallyEmptyList ? (
        <EmptyResults
          icon={<Icon name={'document'} />}
          header={t('audits::totally_empty_results.header')}
          text={t('audits::totally_empty_results.text')}
        />
      ) : (
        <>
          <PageHeader
            className={'margin-bottom-24'}
            color={'Label-Primary'}
          >
            {t('audits::header')}
          </PageHeader>
          <FilterPanel
            className={'margin-bottom-32'}
            filterManager={filterManager}
          />

          <GroupActions
            buttonLabel={t(`audits::group_actions.${action}`)}
            data={data}
            checkboxHandleChange={checkboxHandleChange}
            checkedIDsLength={checkedIDs.length}
            archiveHandleClick={() =>
              new Promise((resolve, reject) => {
                dispatch({
                  meta: { resolve, reject },
                  type: TOGGLE_ARCHIVE,
                  payload: {
                    ids: checkedIDs,
                    action,
                  },
                })
              }).then(() => setCheckedIDs([]))
            }
          />

          {data.length === 0 && loading ? (
            <div className={styles['recommendations-list__spinner-container']}>
              <GridSpinner
                width={32}
                style={{ height: '100%' }}
              />
            </div>
          ) : (
            <>
              {filterManager.filters && (
                <List
                  processingIds={archive.processingIds}
                  data={data}
                  loading={loading}
                  filters={filterManager.filters}
                  checkboxHandleChange={checkboxHandleChange}
                  checkedIDs={checkedIDs}
                />
              )}
              <ShowMoreButton
                onClick={handleChangePage}
                loading={loading}
                current={data.length}
                total={pagination.total}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}

RecommendationsList.propTypes = {}

export default RecommendationsList
